import React, { useState, useEffect, useContext } from "react";
import { Title } from "src/pages/Messaging/components/Title";
import { Grid } from "@mui/material";
import { useTitle } from "src/hooks/useTitle";
import Button from "@jbhi-fi/lanyard-ui/components/Button/Button";
import TextField from "@jbhi-fi/lanyard-ui/components/TextField/TextField";
import { SelectField } from "src/components/Form";
import {
  ExceptionsSearchOptions,
  ExceptionsSearchOptionsType,
} from "src/models/staffPriceExceptions/StaffPriceExceptionsSearchOptions";
import { SearchOutlined, ClearOutlined, EditOutlined } from "@mui/icons-material";
import DataGrid from "../Product/Desktop/components/DataGrid";
import makeStyles from "@mui/styles/makeStyles";
import { GridCellParams, GridColDef } from "@mui/x-data-grid-pro";
import { useHistory } from "react-router-dom";
import { getConsts } from "src/utils/consts";
import clsx from "clsx";
import { ConfirmModal, ConfirmModalState } from "src/components/Modal";
import { useCountry } from "src/components/Context/CountryContext";
import { getLatestExceptionsAsync } from "src/utils/productAppApiHelper";
import { upsertStaffPriceExceptionsAsync } from "src/infrastructure/http/appApiService";
import { Controller, useFormContext, useForm, FormProvider } from "react-hook-form";
import { AutoCompleteType, InputType } from "@jbhi-fi/lanyard-ui/components/TextField/TextField.types";
import PageContext from "src/components/Context/PageContext";
import { hasPermission } from "src/infrastructure/auth/PermissionUtils";
import { UserRoles } from "src/infrastructure/auth/UserRoles";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  cellRender: {
    "& .cellRender.active": {
      color: theme.palette.success.main,
      fontWeight: 700,
    },

    "& .cellRender.inactive": {
      color: theme.palette.error.main,
      fontWeight: 700,
    },

    "& .cellRender.future": {
      color: theme.palette.warning.main,
      fontWeight: 700,
    },
  },
}));

export const StaffExceptions: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const consts = getConsts();
  const { country, timezone } = useCountry();
  const [searchParameter, setSearchParameter] = useState<ExceptionsSearchOptions>(ExceptionsSearchOptions.sku);
  const [searchString, setSearchString] = useState<string>("");
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [searchResults, setSearchResults] = useState<any>([]);
  const [filteredResults, setFilteredResults] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [confirmModalState, setConfirmModalState] = useState<ConfirmModalState>({
    show: false,
  });
  const [editRow, setEditRow] = useState<any | null>(null);
  const [contextData] = useContext(PageContext);
  const { userProfile } = contextData;

  useTitle("Staff Price Exceptions");

  const fetchStaffPriceExceptions = async () => {
    try {
      setIsLoading(true);
      const result = await getLatestExceptionsAsync(country);
      if (result === null) {
        setHasError(true);
        setErrorMessage("There has been an error fetching the data. Please try again later.");
        setSearchResults([]);
        setFilteredResults([]);
      } else {
        // Reverse the order of the results to show newest first
        const reversedResults = [...result].reverse();
        setSearchResults(reversedResults);
        setFilteredResults(reversedResults);
      }
    } catch (e) {
      setHasError(true);
      setErrorMessage("There has been an error fetching the data. Please try again later.");
      setSearchResults([]);
      setFilteredResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStaffPriceExceptions();
  }, [country]);

  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      price: "",
      comment: "",
    },
  });

  useEffect(() => {
    if (editRow) {
      methods.reset({
        price: "",
        comment: "",
      });
      methods.clearErrors();
    }
  }, [editRow]);

  const EditConfirmationForm = () => {
    const formContext = useFormContext();
    const {
      formState: { errors },
    } = formContext;

    return (
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <label>New SKU Price</label>
          <Controller
            name="price"
            control={formContext.control}
            rules={{
              required: "Price is required",
              validate: {
                validNumber: (value) => {
                  if (!value || value.trim() === "") return "Price is required";
                  const numValue = parseFloat(value);
                  if (isNaN(numValue)) return "Please enter a valid number";
                  if (numValue < 0) return "Price must be non-negative";
                  if (!/^\d+(\.\d{1,2})?$/.test(value)) return "Price can have at most 2 decimal places";
                  return true;
                },
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                id={field.name}
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                type={InputType.number}
                min="0"
                autoComplete={AutoCompleteType.off}
                hasError={!!error}
                errorMsg={error?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={6}>
          <label>New Comment</label>
          <Controller
            name="comment"
            control={formContext.control}
            rules={{
              required: "Comment is required",
              maxLength: {
                value: 100,
                message: "Comment must be at most 100 characters",
              },
              validate: {
                notEmpty: (value) => {
                  if (!value || value.trim().length === 0) {
                    return "Comment cannot be empty";
                  }
                  return true;
                },
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                id={field.name}
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                autoComplete={AutoCompleteType.off}
                hasError={!!error}
                errorMsg={error?.message}
              />
            )}
          />
        </Grid>
      </Grid>
    );
  };

  const endConfirmationStatus = {
    title: "Are you sure you want to end this staff price exception now?",
    confirmLabel: "Yes",
    cancelLabel: "No, go back",
  };

  const onCreateButtonClick = () => {
    history.push(consts.routerPath.newException);
  };

  const handleSearch = () => {
    if (!searchString || searchString.trim() === "") {
      setFilteredResults(searchResults);
      return;
    }

    const query = searchString.toLowerCase().trim();
    const filtered = searchResults.filter((exception: any) => {
      const details = exception.details ? exception.details.toLowerCase() : "";
      const exceptionType = exception.type ? exception.type.toLowerCase() : "";

      if (searchParameter === ExceptionsSearchOptions.sku) {
        return exceptionType === "sku" && details.includes(query);
      } else {
        return exceptionType === "store" && details.includes(query);
      }
    });

    setFilteredResults(filtered);

    if (filtered.length === 0) {
      setHasError(true);
      setErrorMessage(
        `No exceptions found with ${
          searchParameter === ExceptionsSearchOptions.sku ? "SKU" : "Store"
        } containing "${searchString}"`
      );
    } else {
      setHasError(false);
      setErrorMessage(undefined);
    }
  };

  // Effect to reset filtered results when search parameter changes
  useEffect(() => {
    if (searchString) {
      handleSearch();
    }
  }, [searchParameter]);

  const columns: GridColDef[] = [
    {
      field: "type",
      headerName: "Type",
      flex: 2,
    },
    {
      field: "details",
      headerName: "Details",
      flex: 2,
    },
    {
      field: "price",
      headerName: "Price",
      flex: 2,
      renderCell: (params: GridCellParams<any>) => {
        const value = params.value;
        return value !== undefined && value !== null ? `$${value}` : "N/A";
      },
    },
    {
      field: "authorisedBy",
      headerName: "Authorised by",
      flex: 3,
    },
    {
      field: "comment",
      headerName: "Comment",
      flex: 5,
    },
    {
      field: "isActive",
      headerName: "Status",
      flex: 2,
      renderCell: (params: GridCellParams<any>) => {
        const isActive = params.value === true;
        return (
          <span
            className={clsx("cellRender", {
              active: isActive,
              inactive: !isActive,
            })}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      field: "startDate",
      headerName: "Start Date",
      valueFormatter: (params) =>
        params.value ? moment.utc(params.value).tz(timezone).format("DD/MM/YYYY hh:mm A") : "",
      flex: 3,
    },
    {
      field: "endDate",
      headerName: "End Date",
      valueFormatter: (params) =>
        params.value ? moment.utc(params.value).tz(timezone).format("DD/MM/YYYY hh:mm A") : "",
      flex: 3,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 2,
      renderCell: ({ row }) => {
        if (row.type === "STORE") {
          return <></>;
        }

        return (
          <>
            <Button
              type={"iconOnly"}
              icon={<ClearOutlined />}
              onClick={() => {
                setConfirmModalState({
                  ...endConfirmationStatus,
                  show: true,
                  onConfirm: async () => {
                    try {
                      await upsertStaffPriceExceptionsAsync({ ...row, isActive: false }, country);

                      fetchStaffPriceExceptions();

                      setConfirmModalState({ show: false });
                    } catch (error) {
                      console.error("Failed to update exception:", error);
                      setHasError(true);
                      setErrorMessage("Failed to update the exception. Please try again.");
                      setConfirmModalState({ show: false });
                    }
                  },
                  onCancel: () => {
                    setConfirmModalState({ show: false });
                  },
                });
              }}
            />
            <Button
              type={"iconOnly"}
              icon={<EditOutlined />}
              onClick={() => {
                setEditRow(row);
                setConfirmModalState({
                  title: "Edit staff price exception",
                  message: (
                    <FormProvider {...methods}>
                      <EditConfirmationForm />
                    </FormProvider>
                  ),
                  confirmLabel: "Update",
                  cancelLabel: "No, go back",
                  show: true,
                  onConfirm: async () => {
                    const isValid = await methods.trigger();
                    if (isValid) {
                      const formData = methods.getValues();
                      const updatedRow = {
                        ...row,
                        price: formData.price,
                        comment: formData.comment,
                      };
                      await upsertStaffPriceExceptionsAsync(updatedRow, country);
                      fetchStaffPriceExceptions();
                      setConfirmModalState({ show: false });
                      methods.reset();
                      methods.clearErrors();
                    }
                  },
                  onCancel: () => {
                    setConfirmModalState({ show: false });
                    methods.reset();
                    methods.clearErrors();
                  },
                });
              }}
            />
          </>
        );
      },
    },
  ];

  if (!hasPermission(UserRoles.PricingStaffExceptions, userProfile.roles)) {
    window.location.href = "/";
    return null;
  }

  return (
    <Grid container justifySelf={"center"} paddingTop={5} spacing={3}>
      <Grid item xs={9} paddingBottom={4}>
        <Title>Staff Price Exceptions</Title>
      </Grid>

      <Grid item xs={3} alignContent={"right"}>
        <Button type={"primary"} label={"Create New Exception"} onClick={onCreateButtonClick} fullWidth />
      </Grid>

      <Grid item xs={2}>
        <SelectField
          name={"searchParameter"}
          label={"Search By"}
          onChange={(value) => {
            setSearchParameter(value);
            setHasError(false);
            setErrorMessage(undefined);
          }}
          value={searchParameter}
          options={ExceptionsSearchOptionsType}
        />
      </Grid>

      <Grid item xs={8}>
        <TextField
          id={"exceptionSearchBar"}
          name={"exceptionSearchBar"}
          icon={<SearchOutlined style={{ paddingRight: "4px" }} />}
          placeholder={
            searchParameter === ExceptionsSearchOptions.sku
              ? "Search all exceptions by SKU"
              : "Search all exceptions by store"
          }
          value={searchString}
          onChange={(e) => {
            setSearchString(e.target.value);
            setHasError(false);
            setErrorMessage(undefined);

            // Clear error if search field is emptied
            if (!e.target.value) {
              setFilteredResults(searchResults);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSearch();
            }
          }}
          hasError={hasError}
          errorMsg={errorMessage}
        />
      </Grid>

      <Grid item xs={2}>
        <Button type={"secondary"} label={"Search"} onClick={handleSearch} fullWidth />
      </Grid>

      <Grid item xs={12} className={classes.cellRender}>
        <DataGrid
          columns={columns}
          rows={filteredResults && filteredResults.length > 0 ? filteredResults : []}
          isLoading={isLoading}
          pagination
          defaultPageSize={25}
          autoHeight
        />
      </Grid>

      <ConfirmModal state={confirmModalState} />
    </Grid>
  );
};
